import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, Legend, Cell, PieChart, Pie, ComposedChart, Sector } from 'recharts';
import { scaleLinear } from 'd3-scale';
import BestMap from './BestMap';
import './AirExportInsights.css';
import { ArrowLeft, BarChart3, Globe, Package, RefreshCw, Users, Plane, Ship, Layers3, ArrowUpRight, Calendar, X, PieChart as PieIcon, Search, Download } from 'lucide-react';
import { DateRangePicker } from 'react-date-range';
import { buildCsv } from '../../utils/exportCsv';
import { startOfDay, endOfDay } from 'date-fns';
import { DATE_PRESETS } from '../common/InvoiceFilters';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import UserShell from '../Layout/UserShell';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { API_ENDPOINTS, apiCall } from '../../config/api';
import {
  gaInsightsLoad,
  gaInsightsTabSwitch,
  gaInsightsYearFilter,
  gaInsightsMonthDrilldown,
  gaInsightsCountryDrilldown,
  gaInsightsResetFilter,
} from '../../utils/analytics';


const INSIGHT_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'performance', label: 'Global Presence' },
  { id: 'compliance', label: 'Top Clients' },
  { id: 'forecasts', label: 'Incentive Insights' },
  { id: 'igst', label: 'IGST' },
];

// `dark:${tab.bgDark}` built the class name at runtime, which Tailwind never
// sees, so the unpicked cards had no dark variant at all — that is why Both
// sat there as a white slab in the dark console. Every class is written out.
const TRANSPORT_TABS = [
  {
    id: 'air',
    label: 'Air',
    subLabel: 'Export',
    icon: Plane,
    on: 'border-blue-700/30 from-blue-500 to-blue-600 shadow-blue-600/25 hover:shadow-blue-600/35',
    off: 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-blue-500/10 '
       + 'dark:border-slate-800 dark:bg-slate-900/60 dark:hover:border-blue-500/40',
    tile: 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md shadow-blue-600/25',
    arrow: 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300',
    glow: 'bg-blue-500/20',
  },
  {
    id: 'sea',
    label: 'Sea',
    subLabel: 'Export',
    icon: Ship,
    on: 'border-teal-700/30 from-teal-500 to-teal-600 shadow-teal-600/25 hover:shadow-teal-600/35',
    off: 'border-slate-200 bg-white hover:border-teal-300 hover:shadow-teal-500/10 '
       + 'dark:border-slate-800 dark:bg-slate-900/60 dark:hover:border-teal-500/40',
    tile: 'bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-md shadow-teal-600/25',
    arrow: 'bg-teal-50 text-teal-600 dark:bg-teal-500/15 dark:text-teal-300',
    glow: 'bg-teal-500/20',
  },
  {
    id: 'both',
    label: 'Both',
    subLabel: 'Combined',
    icon: Layers3,
    on: 'border-violet-700/30 from-violet-500 to-violet-600 shadow-violet-600/25 hover:shadow-violet-600/35',
    off: 'border-slate-200 bg-white hover:border-violet-300 hover:shadow-violet-500/10 '
       + 'dark:border-slate-800 dark:bg-slate-900/60 dark:hover:border-violet-500/40',
    tile: 'bg-gradient-to-br from-violet-500 to-violet-600 text-white shadow-md shadow-violet-600/25',
    arrow: 'bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300',
    glow: 'bg-violet-500/20',
  }
];

const monthMap = {
  JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
  JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c'];

const parseDate = (value) => {
  if (!value) return null;
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw || typeof raw !== 'string') return null;
  // Strip time component (e.g. "26-MAR-026 00:00:00" → "26-MAR-026")
  const first = raw.split('&')[0].trim().split(' ')[0].split('T')[0];
  if (!first) return null;

  // DD/MM/YYYY or D/M/YYYY
  const ddmmyyyy = first.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (ddmmyyyy) {
    const [_, d, m, y] = ddmmyyyy;
    let year = Number(y);
    if (year < 100) year += 2000;
    const date = new Date(year, Number(m) - 1, Number(d));
    if (!isNaN(date.getTime())) return date;
  }

  // DD-MM-YYYY or D-M-YYYY
  const ddmmyyyyDash = first.match(/^(\d{1,2})-(\d{1,2})-(\d{2,4})$/);
  if (ddmmyyyyDash) {
    const [_, d, m, y] = ddmmyyyyDash;
    let year = Number(y);
    if (year < 100) year += 2000;
    const date = new Date(year, Number(m) - 1, Number(d));
    if (!isNaN(date.getTime())) return date;
  }

  // DD-MMM-YY or DD-MMM-YYYY (e.g. 15-Nov-24 or 15-Nov-2024)
  const ddmmmyy = first.match(/^(\d{1,2})-(\w{3})-(\d{2,4})$/);
  if (ddmmmyy) {
    const day = Number(ddmmmyy[1]);
    const month = monthMap[ddmmmyy[2].toUpperCase()];
    let year = Number(ddmmmyy[3]);
    if (year < 100) year += 2000;
    if (month !== undefined) {
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) return date;
    }
  }

  // DD MMM YYYY (e.g. 15 Nov 2024)
  const ddmmmyyyySpace = first.match(/^(\d{1,2})\s+(\w{3})\s+(\d{2,4})$/);
  if (ddmmmyyyySpace) {
    const day = Number(ddmmmyyyySpace[1]);
    const month = monthMap[ddmmmyyyySpace[2].toUpperCase()];
    let year = Number(ddmmmyyyySpace[3]);
    if (year < 100) year += 2000;
    if (month !== undefined) {
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) return date;
    }
  }

  // YYYY/MM/DD
  const yyyymmdd = first.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
  if (yyyymmdd) {
    const date = new Date(Number(yyyymmdd[1]), Number(yyyymmdd[2]) - 1, Number(yyyymmdd[3]));
    if (!isNaN(date.getTime())) return date;
  }

  // ISO or any standard format fallback (YYYY-MM-DD, timestamps etc.)
  const parsed = new Date(first);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const parseFobValues = (value) => {
  if (!value) return 0;
  const values = Array.isArray(value) ? value : (() => {
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        return value.match(/-?\d+[\d,]*(?:\.\d+)?/g) || [];
      }
      return value.match(/-?\d+[\d,]*(?:\.\d+)?/g) || [];
    }
    return [value];
  })();

  return values.reduce((sum, item) => {
    if (item === null || item === undefined || item === '') return sum;
    const num = Number(String(item).replace(/[₹,]/g, ''));
    return sum + (Number.isFinite(num) ? num : 0);
  }, 0);
};

const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(Math.round(value || 0));

// Lakhs and crores, so a crore-sized total still fits a stat card.
const fmtRangeDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const shortINR = (value) => {
  const v = Math.round(value || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${formatNumber(v)}`;
};

const STAT_TONES = {
  blue:    { tile: 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300',          bar: 'bg-blue-400/70' },
  emerald: { tile: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300', bar: 'bg-emerald-400/70' },
  cyan:    { tile: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-500/15 dark:text-cyan-300',          bar: 'bg-cyan-400/70' },
  violet:  { tile: 'bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300',  bar: 'bg-violet-400/70' },
};

// Decoration, not data — but it must stay still between renders.
const statSpark = (seed) => {
  const out = [];
  let x = (seed % 17) + 7;
  for (let i = 0; i < 7; i++) { x = (x * 29 + 11) % 23; out.push(38 + x * 2.6); }
  return out;
};

const PANEL = 'rounded-2xl bg-white dark:bg-[#111a2e] border border-slate-200/80 dark:border-slate-700/50 p-4 sm:p-5';

// A titled card — every panel on the insights tabs uses one.
const Panel = ({ icon: Icon, title, right, children }) => (
  <section className={PANEL}>
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <h3 className="flex items-center gap-2 text-[15px] font-extrabold text-slate-900 dark:text-white">
        <span className="w-8 h-8 rounded-lg grid place-items-center bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
          <Icon size={16} />
        </span>
        {title}
      </h3>
      {right}
    </div>
    {children}
  </section>
);

// The small two/three-way switch the panels use in their header.
const Segmented = ({ options, value, onChange }) => (
  <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-800/70">
    {options.map(([id, label]) => (
      <button
        key={id}
        type="button"
        onClick={() => onChange(id)}
        className={`px-3 h-8 rounded-lg text-[12px] font-bold transition ${
          value === id
            ? 'bg-blue-600 text-white shadow-sm'
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

const SLICE = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899', '#94a3b8'];

// Donut plus a legend that carries the share and the value, as the design has it.
const SliceTooltip = ({ active, payload, nameKey }) => {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  return (
    <div className="rounded-xl px-3.5 py-2.5 bg-slate-900 text-white shadow-2xl border border-slate-700 min-w-[170px] relative z-50">
      <p className="text-[12.5px] font-bold mb-1.5">{r[nameKey]}</p>
      <div className="flex items-center justify-between gap-5 text-[12px]"><span>FOB Value</span><b>{shortINR(r.totalFob)}</b></div>
      <div className="flex items-center justify-between gap-5 text-[12px] mt-0.5"><span>Shipments</span><b>{formatNumber(r.shipmentCount)}</b></div>
      <div className="flex items-center justify-between gap-5 text-[12px] mt-0.5"><span>Share</span><b>{shareOf(r).toFixed(1)}%</b></div>
    </div>
  );
};

const DonutSplit = ({ rows, nameKey }) => {
  const [hovered, setHovered] = useState(-1);
  const top = rows.slice(0, 6);
  const rest = rows.slice(6);
  const restTotal = rest.reduce((a, r) => a + r.totalFob, 0);
  const data = [...top, ...(restTotal ? [{ [nameKey]: 'Others', totalFob: restTotal, shipmentCount: rest.reduce((a, r) => a + r.shipmentCount, 0), percent: rest.reduce((a, r) => a + shareOf(r), 0) }] : [])];
  const total = data.reduce((a, r) => a + r.totalFob, 0);

  if (!data.length) return <p className="py-10 text-center text-sm text-slate-400">No data in this scope.</p>;

  return (
    <div className="flex flex-col sm:flex-row items-center gap-5">
      <div className="relative w-[190px] h-[190px] flex-shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="totalFob"
              nameKey={nameKey}
              innerRadius={58}
              outerRadius={88}
              paddingAngle={2}
              stroke="none"
              activeIndex={hovered}
              activeShape={(props) => <Sector {...props} outerRadius={props.outerRadius + 6} />}
              onMouseEnter={(_, i) => setHovered(i)}
              onMouseLeave={() => setHovered(-1)}
            >
              {data.map((_, i) => <Cell key={i} fill={SLICE[i % SLICE.length]} />)}
            </Pie>
            <Tooltip content={<SliceTooltip nameKey={nameKey} />} animationDuration={120} wrapperStyle={{ zIndex: 40 }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 z-0 grid place-items-center pointer-events-none text-center">
          <div>
            <p className="text-[15px] font-extrabold text-slate-900 dark:text-white">{shortINR(total)}</p>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Total FOB</p>
          </div>
        </div>
      </div>

      <ul className="flex-1 min-w-0 w-full space-y-2">
        {data.map((r, i) => (
          <li
            key={r[nameKey]}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(-1)}
            className={`flex items-center gap-2.5 text-[12.5px] px-1.5 py-1 -mx-1.5 rounded-lg transition-colors ${
              hovered === i ? 'bg-slate-50 dark:bg-slate-800/70' : ''
            }`}
          >
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: SLICE[i % SLICE.length] }} />
            <span className="flex-1 min-w-0 truncate font-semibold text-slate-700 dark:text-slate-200">{r[nameKey]}</span>
            <span className="w-12 text-right font-bold text-slate-500 dark:text-slate-400">{shareOf(r).toFixed(1)}%</span>
            <span className="w-20 text-right font-bold text-slate-800 dark:text-slate-100">{shortINR(r.totalFob)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Ranked rows with a share bar — used for countries and clients.
// Months across the page's scope: FOB on the left axis, shipment count on the
// right. Clicking a month narrows everything below it, which is how the page
// drills month -> country -> client.
const TrendTooltip = ({ active, payload, label, valueLabel = 'FOB Value', valueFormat = shortINR }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-xl px-3.5 py-2.5 bg-slate-900 text-white shadow-2xl border border-slate-700 min-w-[180px]">
      <p className="text-[12.5px] font-bold mb-1.5">{label}</p>
      <div className="flex items-center justify-between gap-5 text-[12px]">
        <span className="flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-blue-400" />{valueLabel}</span>
        <b>{valueFormat(p.totalFob)}</b>
      </div>
      <div className="flex items-center justify-between gap-5 text-[12px] mt-0.5">
        <span className="flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-emerald-400" />Shipments</span>
        <b>{formatNumber(p.shipmentCount)}</b>
      </div>
    </div>
  );
};

const FilterStrip = ({ label, onClear }) => (
  <div className="flex items-center justify-between gap-3 mb-2.5 px-3 py-2 rounded-xl
                  border border-blue-200 bg-blue-50/70
                  dark:border-blue-500/30 dark:bg-blue-500/10">
    <span className="text-[12px] font-bold text-blue-800 dark:text-blue-200 truncate">
      Showing <span className="font-black">{label}</span> only
    </span>
    <button
      type="button"
      onClick={onClear}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11.5px] font-bold flex-shrink-0
                 text-blue-700 hover:bg-blue-100 transition
                 dark:text-blue-300 dark:hover:bg-blue-500/20"
    >
      <X size={13} /> Clear
    </button>
  </div>
);

const TrendChart = ({ rows, selectedKey, onPick, isDark, valueLabel = 'FOB Value', valueFormat = shortINR, height = 'h-[290px]' }) => {
  if (!rows.length) return <p className="py-14 text-center text-sm text-slate-400">No data in this scope.</p>;
  const axis = isDark ? '#8da2c0' : '#64748b';
  const grid = isDark ? '#273449' : '#e8edf6';
  // Clicking a month on the chart filters too, so it gets the same way out.
  const pickedLabel = selectedKey
    ? (rows.find((r) => r.monthKey === selectedKey)?.month || selectedKey)
    : null;
  return (
    <>
      {onPick && pickedLabel && <FilterStrip label={pickedLabel} onClear={() => onPick(selectedKey)} />}
      <div className={height}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={rows}
          margin={{ top: 8, right: 6, left: -8, bottom: 0 }}
          onClick={(e) => { const k = e?.activePayload?.[0]?.payload?.monthKey; if (k) onPick(k); }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
          <XAxis dataKey="monthLabel" tick={{ fill: axis, fontSize: 11.5 }} tickLine={false} axisLine={{ stroke: grid }} minTickGap={12} />
          <YAxis yAxisId="fob" tick={{ fill: axis, fontSize: 11.5 }} tickFormatter={valueFormat} tickLine={false} axisLine={false} width={68} />
          <YAxis yAxisId="cnt" orientation="right" tick={{ fill: axis, fontSize: 11.5 }} tickLine={false} axisLine={false} width={34} />
          <Tooltip
            content={<TrendTooltip valueLabel={valueLabel} valueFormat={valueFormat} />}
            cursor={{ fill: isDark ? 'rgba(59,130,246,0.10)' : 'rgba(37,99,235,0.07)' }}
            animationDuration={120}
            wrapperStyle={{ zIndex: 40 }}
          />
          <Line
            yAxisId="fob" type="linear" dataKey="totalFob" name={valueLabel} stroke="#2563eb" strokeWidth={2.5}
            dot={({ cx, cy, payload }) => (cx === undefined ? null : (
              <circle key={payload.monthKey} cx={cx} cy={cy}
                r={payload.monthKey === selectedKey ? 6 : 4}
                fill={payload.monthKey === selectedKey ? '#1d4ed8' : '#2563eb'}
                stroke={isDark ? '#111a2e' : '#fff'} strokeWidth={2} style={{ cursor: 'pointer' }} />
            ))}
            activeDot={{ r: 6 }}
          />
          <Line
            yAxisId="cnt" type="linear" dataKey="shipmentCount" name="Shipments" stroke="#10b981" strokeWidth={2.5}
            dot={{ r: 4, fill: '#10b981', stroke: isDark ? '#111a2e' : '#fff', strokeWidth: 2 }}
            activeDot={{ r: 6 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-center gap-5 mt-1 text-[11.5px] font-semibold text-slate-500 dark:text-slate-400">
        <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-full bg-blue-600" />{valueLabel}</span>
        <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-full bg-emerald-500" />Shipments</span>
      </div>
      </div>
    </>
  );
};

const shareOf = (row) => {
  const v = row.percent ?? row.fobPercent ?? row.share ?? 0;
  return Number.isFinite(v) ? v : 0;
};

const RankTable = ({ rows, nameKey, columns, onPick, activeKey }) => {
  if (!rows.length) return <p className="py-10 text-center text-sm text-slate-400">No data in this scope.</p>;
  return (
    <>
      {onPick && activeKey && <FilterStrip label={activeKey} onClear={() => onPick(activeKey)} />}
      <div className="overflow-x-auto">
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="text-slate-400 dark:text-slate-500">
            <th className="text-left font-semibold pb-2 w-7">#</th>
            <th className="text-left font-semibold pb-2">Name</th>
            {columns.map((c) => <th key={c.head} className={`font-semibold pb-2 text-${c.align || 'left'} whitespace-nowrap`}>{c.head}</th>)}
            <th className="text-right font-semibold pb-2 w-28">Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r[nameKey]}
              onClick={onPick ? () => onPick(r[nameKey]) : undefined}
              className={`border-t border-slate-100 dark:border-slate-800 ${onPick ? 'cursor-pointer' : ''} ${
                activeKey === r[nameKey]
                  ? 'bg-blue-50 dark:bg-blue-500/10'
                  : onPick ? 'hover:bg-slate-50 dark:hover:bg-slate-800/60' : ''
              }`}
            >
              <td className="py-2.5 text-slate-400 font-semibold">{i + 1}</td>
              <td className="py-2.5 pr-3 font-bold text-slate-800 dark:text-slate-100 truncate max-w-[190px]" title={r[nameKey]}>{r[nameKey]}</td>
              {columns.map((c) => (
                <td key={c.head} className={`py-2.5 px-2 whitespace-nowrap font-semibold text-slate-600 dark:text-slate-300 text-${c.align || 'left'}`}>
                  {c.cell(r)}
                </td>
              ))}
              <td className="py-2.5 pl-2">
                <div className="flex items-center gap-2 justify-end">
                  <span className="w-16 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <span className="block h-full rounded-full bg-blue-500" style={{ width: `${Math.min(100, shareOf(r))}%` }} />
                  </span>
                  <b className="w-11 text-right text-slate-500 dark:text-slate-400">{shareOf(r).toFixed(1)}%</b>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </>
  );
};

// Horizontal bars, longest first.
const BarList = ({ rows, nameKey, valueOf, format }) => {
  if (!rows.length) return <p className="py-10 text-center text-sm text-slate-400">No data in this scope.</p>;
  const max = Math.max(...rows.map(valueOf), 1);
  return (
    <ul className="space-y-3">
      {rows.map((r) => (
        <li key={r[nameKey]}>
          <div className="flex items-center justify-between gap-3 mb-1">
            <span className="text-[12.5px] font-semibold text-slate-700 dark:text-slate-200 truncate" title={r[nameKey]}>{r[nameKey]}</span>
            <span className="text-[12.5px] font-bold text-slate-800 dark:text-slate-100 whitespace-nowrap">{format(valueOf(r))}</span>
          </div>
          <span className="block h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
            <span className="block h-full rounded-full bg-gradient-to-r from-blue-400 to-blue-600"
                  style={{ width: `${Math.max(4, (valueOf(r) / max) * 100)}%` }} />
          </span>
        </li>
      ))}
    </ul>
  );
};

const InsightStat = ({ icon: Icon, tone, value, title, label, delta, seed }) => (
  <div className="rounded-2xl p-4 bg-white dark:bg-[#111a2e] border border-slate-200/80 dark:border-slate-700/50
                  shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)] transition-shadow">
    <div className="flex items-center gap-3">
      <span className={`w-11 h-11 rounded-xl grid place-items-center flex-shrink-0 ${STAT_TONES[tone].tile}`}>
        <Icon size={20} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[21px] sm:text-[24px] leading-[1.15] font-extrabold text-slate-900 dark:text-white truncate"
           title={title || String(value)}>{value}</p>
        <p className="text-[12.5px] font-medium text-slate-500 dark:text-slate-400 truncate">{label}</p>
      </div>
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        {delta !== undefined && (
          <span className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full text-[11px] font-bold
                           bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
            <ArrowUpRight size={11} />{delta}%
          </span>
        )}
        <span className="flex items-end gap-[3px] h-6" aria-hidden="true">
          {statSpark(seed).map((h, i) => (
            <span key={i} className={`w-[4px] rounded-sm ${STAT_TONES[tone].bar}`} style={{ height: `${Math.min(h, 100)}%` }} />
          ))}
        </span>
      </div>
    </div>
  </div>
);

const formatCurrency = (value) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0
}).format(Math.round(value || 0));

const formatShortCurrency = (value) => {
  const amount = Number(value || 0);
  if (amount >= 1e7) return `₹${(amount / 1e7).toFixed(1)}Cr`;
  if (amount >= 1e5) return `₹${(amount / 1e5).toFixed(1)}L`;
  if (amount >= 1e3) return `₹${(amount / 1e3).toFixed(1)}K`;
  return `₹${formatNumber(amount)}`;
};

const getCurrencySymbol = (code) => {
  const normalized = String(code || '').trim().toUpperCase();
  const symbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    INR: '₹',
    AUD: 'A$',
    CAD: 'C$',
    SGD: 'S$',
    AED: 'AED '
  };
  return symbols[normalized] || '';
};

const formatFcValue = (value, code) => {
  const formatted = formatNumber(value);
  return `$${formatted}`;
};

const getFcTotalsByType = (items = []) => {
  const totals = items.reduce((acc, item) => {
    const type = String(item?.fcType || '').trim().toUpperCase();
    const value = Number(item?.fcValue || 0);
    if (!type || !Number.isFinite(value) || value === 0) return acc;
    acc[type] = (acc[type] || 0) + value;
    return acc;
  }, {});

  return Object.entries(totals)
    .map(([type, value]) => ({ type, value }))
    .sort((a, b) => b.value - a.value);
};

const formatFcTotalsInline = (fcTotalsByType = []) => {
  if (!fcTotalsByType.length) return formatNumber(0);
  if (fcTotalsByType.length === 1) {
    return formatFcValue(fcTotalsByType[0].value, fcTotalsByType[0].type);
  }
  return fcTotalsByType.map((item) => formatFcValue(item.value, item.type)).join(' | ');
};

const groupByKey = (items, keyFn) => {
  return items.reduce((acc, item) => {
    const key = keyFn(item);
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
};

const COUNTRY_ALIASES = {
  // GeoJSON → Standard name mappings
  'UNITED STATES OF AMERICA': 'USA',
  'UNITED STATES': 'USA',
  'RUSSIAN FEDERATION': 'RUSSIA',
  'IRAN (ISLAMIC REPUBLIC OF)': 'IRAN',
  'BOLIVIA (PLURINATIONAL STATE OF)': 'BOLIVIA',
  'VENEZUELA (BOLIVARIAN REPUBLIC OF)': 'VENEZUELA',
  'KOREA, REPUBLIC OF': 'SOUTH KOREA',
  "KOREA, DEMOCRATIC PEOPLE'S REPUBLIC OF": 'NORTH KOREA',
  "LAO PEOPLE'S DEMOCRATIC REPUBLIC": 'LAOS',
  'SYRIAN ARAB REPUBLIC': 'SYRIA',
  'TANZANIA, UNITED REPUBLIC OF': 'TANZANIA',
  'VIET NAM': 'VIETNAM',
  'PALESTINE': 'PALESTINE, STATE OF',
  'PALESTINE, STATE OF': 'PALESTINE, STATE OF',
  'MICRONESIA (FEDERATED STATES OF)': 'MICRONESIA',
  'KOREA': 'SOUTH KOREA',
  'US': 'USA',
  'UAE': 'UNITED ARAB EMIRATES',
  'UK': 'UNITED KINGDOM',
  'HONG KONG': 'HONG KONG',
  'HONG KONG SAR': 'HONG KONG',
  'HONG KONG SAR CHINA': 'HONG KONG',
  'HONGKONG': 'HONG KONG',
  'MYANMAR': 'MYANMAR',
  'BURMA': 'MYANMAR',
  'IVORY COAST': 'CÔTE D\'IVOIRE',
  'CÔTE D\'IVOIRE': 'CÔTE D\'IVOIRE',
  'CZECH REPUBLIC': 'CZECHIA',
  'CZECHIA': 'CZECHIA',
  'MACAO': 'MACAO SAR CHINA',
  'MACAO SAR CHINA': 'MACAO SAR CHINA',
  'EGYPT': 'EGYPT',
  'SAUDI ARABIA': 'SAUDI ARABIA',
  'INDONESIA': 'INDONESIA',
  'MALAYSIA': 'MALAYSIA',
  'SINGAPORE': 'SINGAPORE',
  'THAILAND': 'THAILAND',
  'PHILIPPINES': 'PHILIPPINES',
  'VIETNAM': 'VIETNAM',
  'SOUTH KOREA': 'SOUTH KOREA',
  'NORTH KOREA': 'NORTH KOREA',
  'JAPAN': 'JAPAN',
  'INDIA': 'INDIA',
  'PAKISTAN': 'PAKISTAN',
  'BANGLADESH': 'BANGLADESH',
  'SRI LANKA': 'SRI LANKA',
  'NEPAL': 'NEPAL',
  'MEXICO': 'MEXICO',
  'CANADA': 'CANADA',
  'BRAZIL': 'BRAZIL',
  'ARGENTINA': 'ARGENTINA',
  'CHILE': 'CHILE',
  'AUSTRALIA': 'AUSTRALIA',
  'NEW ZEALAND': 'NEW ZEALAND',
  'GERMANY': 'GERMANY',
  'FRANCE': 'FRANCE',
  'ITALY': 'ITALY',
  'SPAIN': 'SPAIN',
  'POLAND': 'POLAND',
  'NETHERLANDS': 'NETHERLANDS',
  'BELGIUM': 'BELGIUM',
  'SWEDEN': 'SWEDEN',
  'SWITZERLAND': 'SWITZERLAND',
  'NORWAY': 'NORWAY'
};

const normalizeCountry = (country = '') => {
  const normalized = country
    .toString()
    .trim()
    .toUpperCase()
    .replace(/\./g, '')
    .replace(/\s+/g, ' ');
  
  // Check if it's in aliases, and if so, return the aliased version
  return COUNTRY_ALIASES[normalized] || normalized;
};

const getDefaultTransportTab = (location) => {
  const queryTab = new URLSearchParams(location.search).get('tab');
  const stateTab = location.state?.transportTab;
  const normalized = String(queryTab || stateTab || 'air').trim().toLowerCase();
  return TRANSPORT_TABS.some((tab) => tab.id === normalized) ? normalized : 'air';
};

const AirExportInsights = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [activeTransportTab, setActiveTransportTab] = useState(() => getDefaultTransportTab(location));
  const [loading, setLoading] = useState(true);
  const [airShipments, setAirShipments] = useState([]);
  const [seaShipments, setSeaShipments] = useState([]);
  const [selectedMonthKey, setSelectedMonthKey] = useState(null);
  const [pageRange, setPageRange] = useState(null);        // the header's date range
  const [rangeOpen, setRangeOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [incentiveDateRange, setIncentiveDateRange] = useState(null);
  const [incentiveShowDatePicker, setIncentiveShowDatePicker] = useState(false);
  const [incentiveSelectedMonth, setIncentiveSelectedMonth] = useState(null);
  const [worldGeoJSON, setWorldGeoJSON] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    fetch('https://raw.githubusercontent.com/johan/world.geo.json/master/countries.geo.json')
      .then(res => res.json())
      .then(data => setWorldGeoJSON(data))
      .catch(err => console.error('Failed to load map data:', err));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setActiveTransportTab(getDefaultTransportTab(location));
  }, [location]);

  useEffect(() => {
    const fetchShipments = async () => {
      if (!user?.id) return;
      setLoading(true);
      try {
        const [airResponse, seaResponse] = await Promise.all([
          apiCall(`${API_ENDPOINTS.GET_USER_SHIPMENTS}?user_id=${user.id}`, {
            method: 'GET'
          }),
          apiCall(`/sea-export/user/${user.id}`, {
            method: 'GET'
          })
        ]);

        const [airData, seaData] = await Promise.all([
          airResponse.json(),
          seaResponse.json()
        ]);

        const nextAirShipments = airResponse.ok && airData.success && Array.isArray(airData.data)
          ? airData.data
          : [];
        const nextSeaShipments = seaResponse.ok && seaData.success && Array.isArray(seaData.data)
          ? seaData.data
          : [];

        setAirShipments(nextAirShipments);
        setSeaShipments(nextSeaShipments);
        gaInsightsLoad(nextAirShipments.length + nextSeaShipments.length);
      } catch (error) {
        console.error('Failed to fetch shipments:', error);
        setAirShipments([]);
        setSeaShipments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchShipments();
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    const fetchInvoices = async () => {
      try {
        const res = await apiCall(`${API_ENDPOINTS.INVOICE_GET_BY_USER}/${user.id}`, { method: 'GET' });
        const data = await res.json();
        if (res.ok && data.success) setInvoices(data.data || []);
        else if (Array.isArray(data)) setInvoices(data);
      } catch {}
    };
    fetchInvoices();
  }, [user]);

  const normalizedAirShipments = useMemo(() => {
    return airShipments.map((shipment) => {
      // Get S/B Date from array (sbDates is an array)
      let shipmentDate = '';
      if (shipment.sbDates && Array.isArray(shipment.sbDates) && shipment.sbDates.length > 0) {
        shipmentDate = shipment.sbDates[0]; // Take first S/B date
      } else if (shipment.sbDate) {
        shipmentDate = shipment.sbDate;
      } else if (shipment.sb_date) {
        shipmentDate = shipment.sb_date;
      } else {
        shipmentDate = shipment.bkgDate || shipment.bkg_date || shipment.createdAt || shipment.created_at || '';
      }
      
      const dateObj = parseDate(shipmentDate);
      const monthKey = dateObj
        ? `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`
        : 'Unknown';
      const monthLabel = dateObj
        ? dateObj.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        : 'Unknown';
      const monthName = dateObj
        ? dateObj.toLocaleDateString('en-US', { month: 'short' })
        : 'Unknown';
      const yearValue = dateObj
        ? dateObj.getFullYear().toString()
        : 'Unknown';
      return {
        id: shipment.id || shipment.awbNumber || shipment.awbNo,
        awbNo: shipment.awbNumber || shipment.awbNo || shipment.awb_no || '',
        customer: shipment.customerName || shipment.username || shipment.customer || 'Unknown',
        country: shipment.destCountry || shipment.country || 'Unknown',
        fobValue: parseFobValues(shipment.sbFobValues || shipment.fobValue || shipment.fob),
        fcValue: parseFobValues(shipment.fcValue || 0),
        fcType: shipment.fcType || '',
        fcRate: parseFobValues(shipment.fcRate || 0),
        dbk: parseFobValues(shipment.DBK || shipment.dbk || shipment.dbkClaim || 0),
        rodtep: parseFobValues(shipment.Rodtep || shipment.rodtep || shipment.rodtepAmt || 0),
        rosctl: parseFobValues(shipment.Rosctl || shipment.rosctl || shipment.rosctlAmt || 0),
        grossWeight: parseFloat(shipment.grossWeight || shipment.gross_weight || shipment.gw || shipment.totalGrossWeight || 0) || 0,
        netWeight: parseFloat(shipment.netWeight || shipment.net_weight || shipment.nw || shipment.totalNetWeight || 0) || 0,
        igst: shipment.IGST || shipment.igst || [],
        createdAt: dateObj ? dateObj.toISOString() : null,
        transportType: 'air',
        monthKey,
        monthLabel,
        monthName,
        yearValue
      };
    });
  }, [airShipments]);

  const normalizedSeaShipments = useMemo(() => {
    return seaShipments.map((shipment) => {
      const shipmentDate = (shipment.sbDates && Array.isArray(shipment.sbDates) && shipment.sbDates.length > 0)
        ? shipment.sbDates[0]
        : (shipment.sb_date || shipment.sbDate || shipment.leo_date || shipment.leoDate || '');
      const dateObj = parseDate(shipmentDate);
      const monthKey = dateObj
        ? `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`
        : 'Unknown';
      const monthLabel = dateObj
        ? dateObj.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        : 'Unknown';
      const monthName = dateObj
        ? dateObj.toLocaleDateString('en-US', { month: 'short' })
        : 'Unknown';
      const yearValue = dateObj
        ? dateObj.getFullYear().toString()
        : 'Unknown';

      return {
        id: shipment.id || shipment.bl_number || shipment.blNumber,
        awbNo: shipment.bl_number || shipment.blNumber || '',
        customer: shipment.consignee_name || shipment.consigneeName || shipment.exporter_name || shipment.exporterName || shipment.buyer_name || shipment.buyerName || 'Unknown',
        country: shipment.country_of_final_destination || shipment.countryOfFinalDestination || 'Unknown',
        fobValue: parseFobValues(shipment.fob_value || shipment.fobValue || 0),
        fcValue: parseFobValues(shipment.fc_value || shipment.fcValue || shipment.inv_amt || shipment.invAmt || shipment.invoiceAmount || 0),
        fcType: shipment.currency || shipment.fcType || '',
        fcRate: parseFobValues(shipment.fc_rate || shipment.fcRate || 0),
        dbk: parseFobValues(shipment.dbk_claim || shipment.dbkClaim || 0),
        rodtep: parseFobValues(shipment.rodtep_amt || shipment.rodtepAmt || 0),
        rosctl: parseFobValues(shipment.rosctl_amt || shipment.rosctlAmt || 0),
        grossWeight: parseFloat(shipment.gross_weight || shipment.grossWeight || shipment.total_gross_weight || shipment.totalGrossWeight || 0) || 0,
        netWeight: parseFloat(shipment.net_weight || shipment.netWeight || shipment.total_net_weight || shipment.totalNetWeight || 0) || 0,
        igst: shipment.IGST || shipment.igst || [],
        createdAt: dateObj ? dateObj.toISOString() : null,
        transportType: 'sea',
        monthKey,
        monthLabel,
        monthName,
        yearValue
      };
    });
  }, [seaShipments]);

  const scopedShipments = useMemo(() => {
    if (activeTransportTab === 'sea') return normalizedSeaShipments;
    if (activeTransportTab === 'both') return [...normalizedAirShipments, ...normalizedSeaShipments];
    return normalizedAirShipments;
  }, [activeTransportTab, normalizedAirShipments, normalizedSeaShipments]);

  // Every table, chart and total on this page derives from normalizedShipments,
  // so the page's date range is applied here once, on the shipment's S/B date.
  const normalizedShipments = useMemo(() => {
    const from = pageRange?.[0]?.startDate;
    const to = pageRange?.[0]?.endDate;
    if (!from || !to) return scopedShipments;
    const lo = startOfDay(from).getTime();
    const hi = endOfDay(to).getTime();
    return scopedShipments.filter((item) => {
      const t = item.createdAt ? new Date(item.createdAt).getTime() : NaN;
      return Number.isFinite(t) && t >= lo && t <= hi;
    });
  }, [scopedShipments, pageRange]);

  const monthData = useMemo(() => {
    const grouped = groupByKey(normalizedShipments, (item) => item.monthKey);
    const data = Object.entries(grouped).map(([monthKey, items]) => {
      const totalFob = items.reduce((sum, item) => sum + item.fobValue, 0);
      const totalFc = items.reduce((sum, item) => sum + item.fcValue, 0);
      const fcTotalsByType = getFcTotalsByType(items);
      return {
        monthKey,
        monthLabel: items[0]?.monthLabel || monthKey,
        monthName: items[0]?.monthName || monthKey.split('-')[1],
        yearValue: items[0]?.yearValue || monthKey.split('-')[0],
        totalFob,
        totalFc,
        fcTotalsByType,
        shipmentCount: items.length
      };
    });

    return data
      .filter((item) => item.monthKey !== 'Unknown')
      .sort((a, b) => (a.monthKey > b.monthKey ? 1 : -1));
  }, [normalizedShipments]);

  const monthDataFiltered = useMemo(() => {
    if (!selectedYear) return monthData;
    return monthData.filter((item) => item.yearValue === selectedYear);
  }, [monthData, selectedYear]);

  useEffect(() => {
    if (selectedMonthKey && !monthDataFiltered.find((item) => item.monthKey === selectedMonthKey)) {
      setSelectedMonthKey(null);
      setSelectedCountry(null);
    }
  }, [monthDataFiltered, selectedMonthKey]);

  const [trendBy, setTrendBy] = useState('month');   // the trend chart's grouping
  const [clientBy, setClientBy] = useState('fob');  // how the client bars rank
  const [clientSearch, setClientSearch] = useState('');
  const [incentiveSearch, setIncentiveSearch] = useState('');

  // selectedMonthKey holds whichever bucket the trend chart is grouped by —
  // "2026-06" for a month, "2026-Q2" for a quarter, "2026" for a year — so one
  // selection drives the scope whatever the grouping is.
  const matchesPeriod = (monthKey, bucket) => {
    if (!bucket || !monthKey) return !bucket;
    if (bucket.includes('-Q')) {
      const [y, q] = bucket.split('-Q');
      const [my, mm] = monthKey.split('-');
      return my === y && String(Math.floor((Number(mm) - 1) / 3) + 1) === q;
    }
    if (bucket.length === 4) return monthKey.startsWith(bucket);
    return monthKey === bucket;
  };

  const filteredByMonth = useMemo(() => {
    if (!selectedMonthKey) return normalizedShipments;
    return normalizedShipments.filter((item) => matchesPeriod(item.monthKey, selectedMonthKey));
  }, [normalizedShipments, selectedMonthKey]);

  // monthData rolled up to whichever period the trend chart is showing.
  const trendRows = useMemo(() => {
    if (trendBy === 'month') return monthDataFiltered;
    const buckets = new Map();
    monthDataFiltered.forEach((m) => {
      const [y, mm] = m.monthKey.split('-');
      const key = trendBy === 'year' ? y : `${y}-Q${Math.floor((Number(mm) - 1) / 3) + 1}`;
      const label = trendBy === 'year' ? y : `Q${Math.floor((Number(mm) - 1) / 3) + 1} ${y}`;
      const row = buckets.get(key) || { monthKey: key, monthLabel: label, totalFob: 0, shipmentCount: 0 };
      row.totalFob += m.totalFob;
      row.shipmentCount += m.shipmentCount;
      buckets.set(key, row);
    });
    return [...buckets.values()];
  }, [monthDataFiltered, trendBy]);

  const countryData = useMemo(() => {
    const grouped = groupByKey(filteredByMonth, (item) => item.country || 'Unknown');
    const totalFob = filteredByMonth.reduce((sum, item) => sum + item.fobValue, 0);
    const totalFc = filteredByMonth.reduce((sum, item) => sum + item.fcValue, 0);

    return Object.entries(grouped)
      .map(([country, items]) => {
        const value = items.reduce((sum, item) => sum + item.fobValue, 0);
        const fcValue = items.reduce((sum, item) => sum + item.fcValue, 0);
        const fcTotalsByType = getFcTotalsByType(items);
        return {
          country,
          totalFob: value,
          totalFc: fcValue,
          fcTotalsByType,
          shipmentCount: items.length,
          percent: totalFob ? (value / totalFob) * 100 : 0,
          fcPercent: totalFc ? (fcValue / totalFc) * 100 : 0
        };
      })
      .sort((a, b) => b.percent - a.percent);
  }, [filteredByMonth]);

  const globalCountryData = useMemo(() => {
    const grouped = groupByKey(normalizedShipments, (item) => item.country || 'Unknown');
    const totalFobAll = normalizedShipments.reduce((sum, item) => sum + item.fobValue, 0);
    const totalDbkAll = normalizedShipments.reduce((sum, item) => sum + item.dbk, 0);
    const totalRodtepAll = normalizedShipments.reduce((sum, item) => sum + item.rodtep, 0);
    const totalRosctlAll = normalizedShipments.reduce((sum, item) => sum + item.rosctl, 0);

    const data = Object.entries(grouped)
      .map(([country, items]) => {
        const fobValue = items.reduce((sum, item) => sum + item.fobValue, 0);
        const totalFc = items.reduce((sum, item) => sum + item.fcValue, 0);
        const fcTotalsByType = getFcTotalsByType(items);
        const dbkValue = items.reduce((sum, item) => sum + item.dbk, 0);
        const rodtepValue = items.reduce((sum, item) => sum + item.rodtep, 0);
        const rosctlValue = items.reduce((sum, item) => sum + item.rosctl, 0);
        return {
          country,
          totalFob: fobValue,
          totalFc,
          fcTotalsByType,
          totalDbk: dbkValue,
          totalRodtep: rodtepValue,
          totalRosctl: rosctlValue,
          shipmentCount: items.length,
          fobPercent: totalFobAll ? (fobValue / totalFobAll) * 100 : 0,
          dbkPercent: totalDbkAll ? (dbkValue / totalDbkAll) * 100 : 0,
          rodtepPercent: totalRodtepAll ? (rodtepValue / totalRodtepAll) * 100 : 0,
          rosctlPercent: totalRosctlAll ? (rosctlValue / totalRosctlAll) * 100 : 0
        };
      })
      .sort((a, b) => b.fobPercent - a.fobPercent);
    
    console.log('=== COUNTRY DATA FOR MAP ===');
    console.log('Total FOB:', totalFobAll);
    console.log('Total Countries with Data:', data.length);
    console.table(data.map(d => ({
      Country: d.country,
      'FOB Value': d.totalFob,
      'FOB %': d.fobPercent.toFixed(2),
      'DBK': d.totalDbk,
      'RODTEP': d.totalRodtep,
      'ROSCTL': d.totalRosctl,
      Shipments: d.shipmentCount
    })));
    
    return data;
  }, [normalizedShipments]);

  const combinedCountryData = useMemo(() => {
    const grouped = groupByKey(normalizedShipments, (item) => item.country || 'Unknown');
    const totalCombinedAll = normalizedShipments.reduce(
      (sum, item) => sum + (item.dbk || 0) + (item.rodtep || 0) + (item.rosctl || 0),
      0
    );

    const result = Object.entries(grouped)
      .map(([country, items]) => {
        const combinedValue = items.reduce(
          (sum, item) => sum + (item.dbk || 0) + (item.rodtep || 0) + (item.rosctl || 0),
          0
        );
        return {
          country,
          value: combinedValue,
          shipmentCount: items.length,
          percent: totalCombinedAll ? (combinedValue / totalCombinedAll) * 100 : 0
        };
      })
      .filter((item) => item.value > 0)
      .sort((a, b) => b.percent - a.percent);
    
    return result;
  }, [normalizedShipments]);

  const dbkCountryData = useMemo(() => {
    return globalCountryData
      .map((item) => ({
        country: item.country,
        value: item.totalDbk || 0,
        percent: item.dbkPercent || 0,
        shipmentCount: item.shipmentCount || 0
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.percent - a.percent);
  }, [globalCountryData]);

  const rodtepCountryData = useMemo(() => {
    return globalCountryData
      .map((item) => ({
        country: item.country,
        value: item.totalRodtep || 0,
        percent: item.rodtepPercent || 0,
        shipmentCount: item.shipmentCount || 0
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.percent - a.percent);
  }, [globalCountryData]);

  const totalIncentiveValue = useMemo(() => {
    return combinedCountryData.reduce((sum, item) => sum + item.value, 0);
  }, [combinedCountryData]);

  // ── Incentives tab filtered data ──
  const incentiveShipments = useMemo(() => {
    let list = normalizedShipments;
    if (incentiveDateRange?.[0]?.startDate && incentiveDateRange?.[0]?.endDate) {
      const start = new Date(incentiveDateRange[0].startDate); start.setHours(0, 0, 0, 0);
      const end = new Date(incentiveDateRange[0].endDate); end.setHours(23, 59, 59, 999);
      list = list.filter(s => {
        const d = s.createdAt ? new Date(s.createdAt) : null;
        if (!d) return false;
        return d >= start && d <= end;
      });
    }
    if (incentiveSelectedMonth) {
      list = list.filter(s => s.monthLabel === incentiveSelectedMonth || s.monthKey === incentiveSelectedMonth);
    }
    return list;
  }, [normalizedShipments, incentiveDateRange, incentiveSelectedMonth]);

  const incentiveGlobalCountryData = useMemo(() => {
    const grouped = groupByKey(incentiveShipments, (item) => item.country || 'Unknown');
    const totalFobAll = incentiveShipments.reduce((sum, item) => sum + item.fobValue, 0);
    const totalDbkAll = incentiveShipments.reduce((sum, item) => sum + item.dbk, 0);
    const totalRodtepAll = incentiveShipments.reduce((sum, item) => sum + item.rodtep, 0);
    const totalRosctlAll = incentiveShipments.reduce((sum, item) => sum + item.rosctl, 0);
    return Object.entries(grouped)
      .map(([country, items]) => ({
        country,
        totalFob: items.reduce((sum, item) => sum + item.fobValue, 0),
        totalDbk: items.reduce((sum, item) => sum + item.dbk, 0),
        totalRodtep: items.reduce((sum, item) => sum + item.rodtep, 0),
        totalRosctl: items.reduce((sum, item) => sum + item.rosctl, 0),
        shipmentCount: items.length,
        fobPercent: totalFobAll ? (items.reduce((s, i) => s + i.fobValue, 0) / totalFobAll) * 100 : 0,
        dbkPercent: totalDbkAll ? (items.reduce((s, i) => s + i.dbk, 0) / totalDbkAll) * 100 : 0,
        rodtepPercent: totalRodtepAll ? (items.reduce((s, i) => s + i.rodtep, 0) / totalRodtepAll) * 100 : 0,
        rosctlPercent: totalRosctlAll ? (items.reduce((s, i) => s + i.rosctl, 0) / totalRosctlAll) * 100 : 0,
      }))
      .sort((a, b) => b.fobPercent - a.fobPercent);
  }, [incentiveShipments]);

  const incentiveCombinedCountryData = useMemo(() => {
    const grouped = groupByKey(incentiveShipments, (item) => item.country || 'Unknown');
    const totalAll = incentiveShipments.reduce((sum, item) => sum + (item.dbk || 0) + (item.rodtep || 0) + (item.rosctl || 0), 0);
    return Object.entries(grouped)
      .map(([country, items]) => {
        const value = items.reduce((sum, item) => sum + (item.dbk || 0) + (item.rodtep || 0) + (item.rosctl || 0), 0);
        return { country, value, shipmentCount: items.length, percent: totalAll ? (value / totalAll) * 100 : 0 };
      })
      .filter(item => item.value > 0)
      .sort((a, b) => b.percent - a.percent);
  }, [incentiveShipments]);

  const incentiveTotalValue = useMemo(() =>
    incentiveCombinedCountryData.reduce((sum, item) => sum + item.value, 0),
  [incentiveCombinedCountryData]);

  const incentiveAvailableMonths = useMemo(() =>
    [...new Set(normalizedShipments.map(s => s.monthLabel || s.monthKey).filter(Boolean))].sort(),
  [normalizedShipments]);

  const incentiveTableData = useMemo(() => {
    const combinedLookup = new Map(incentiveCombinedCountryData.map((item) => [item.country, item]));
    return incentiveGlobalCountryData
      .map((item) => {
        const total = (item.totalDbk || 0) + (item.totalRodtep || 0) + (item.totalRosctl || 0);
        const combined = combinedLookup.get(item.country);
        const percent = incentiveTotalValue ? (total / incentiveTotalValue) * 100 : (combined?.percent || 0);
        return { country: item.country, totalDbk: item.totalDbk || 0, totalRodtep: item.totalRodtep || 0, totalRosctl: item.totalRosctl || 0, total, percent };
      })
      .filter((item) => item.total > 0)
      .sort((a, b) => b.percent - a.percent);
  }, [incentiveGlobalCountryData, incentiveCombinedCountryData, incentiveTotalValue]);

  const complianceCustomerData = useMemo(() => {
    const grouped = groupByKey(normalizedShipments, (item) => item.customer || 'Unknown');
    const totalFobAll = normalizedShipments.reduce((sum, item) => sum + item.fobValue, 0);

    return Object.entries(grouped)
      .map(([customer, items]) => {
        const value = items.reduce((sum, item) => sum + item.fobValue, 0);
        const fcValue = items.reduce((sum, item) => sum + item.fcValue, 0);
        const fcTotalsByType = getFcTotalsByType(items);
        return {
          customer,
          totalFob: value,
          totalFc: fcValue,
          fcTotalsByType,
          shipmentCount: items.length,
          percent: totalFobAll ? (value / totalFobAll) * 100 : 0
        };
      })
      .sort((a, b) => b.percent - a.percent);
  }, [normalizedShipments]);

  const complianceFcTotalsByType = useMemo(() => getFcTotalsByType(normalizedShipments), [normalizedShipments]);

  const countryDataLookup = useMemo(() => {
    const lookup = new Map();
    globalCountryData.forEach((item) => {
      const key = normalizeCountry(item.country);
      if (key) lookup.set(key, item);
    });
    return lookup;
  }, [globalCountryData]);

  const bigCountrySet = useMemo(() => {
    return new Set([
      'UNITED STATES OF AMERICA',
      'UNITED STATES',
      'USA',
      'INDIA',
      'CHINA',
      'RUSSIA',
      'CANADA',
      'BRAZIL',
      'AUSTRALIA'
    ].map(normalizeCountry));
  }, []);

  const colorScale = useMemo(() => {
    const maxValue = Math.max(...globalCountryData.map((item) => item.totalFob), 0);
    return scaleLinear()
      .domain([0, maxValue || 1])
      .range(['#a7f3d0', '#059669']);  // Light mint to deep teal - better visual hierarchy
  }, [globalCountryData]);

  const highlightCountry = (country) => setHoveredCountry(country);
  const resetCountry = () => setHoveredCountry(null);

  const handleMonthClick = (payload) => {
    if (!payload?.monthKey) return;
    setSelectedMonthKey(payload.monthKey);
    setSelectedCountry(null);
    gaInsightsMonthDrilldown(payload.monthKey, activeTab);
  };

  const handleCountryClick = (payload) => {
    if (!payload?.country) return;
    setSelectedCountry(payload.country);
    gaInsightsCountryDrilldown(payload.country, activeTab);
  };
  const filteredByCountry = useMemo(() => {
    if (!selectedCountry) return filteredByMonth;
    return filteredByMonth.filter((item) => item.country === selectedCountry);
  }, [filteredByMonth, selectedCountry]);

  const customerData = useMemo(() => {
    const grouped = groupByKey(filteredByCountry, (item) => item.customer || 'Unknown');
    const totalFob = filteredByCountry.reduce((sum, item) => sum + item.fobValue, 0);
    const totalFc = filteredByCountry.reduce((sum, item) => sum + item.fcValue, 0);

    return Object.entries(grouped)
      .map(([customer, items]) => {
        const value = items.reduce((sum, item) => sum + item.fobValue, 0);
        const fcValue = items.reduce((sum, item) => sum + item.fcValue, 0);
        const fcTotalsByType = getFcTotalsByType(items);
        return {
          customer,
          totalFob: value,
          totalFc: fcValue,
          fcTotalsByType,
          shipmentCount: items.length,
          percent: totalFob ? (value / totalFob) * 100 : 0,
          fcPercent: totalFc ? (fcValue / totalFc) * 100 : 0
        };
      })
      .sort((a, b) => b.percent - a.percent);
  }, [filteredByCountry]);

  const currentScope = selectedCountry ? filteredByCountry : filteredByMonth;
  
  // For DBK & RODTEP tab, show ALL data; for other tabs, show filtered data
  const displayScope = currentScope;
  const totalFob = displayScope.reduce((sum, item) => sum + item.fobValue, 0);
  const totalFc = displayScope.reduce((sum, item) => sum + item.fcValue, 0);

  const fcTotalsByType = useMemo(() => {
    const totals = displayScope.reduce((acc, item) => {
      const type = String(item.fcType || '').trim().toUpperCase();
      const value = Number(item.fcValue || 0);
      if (!type || !Number.isFinite(value) || value === 0) return acc;
      acc[type] = (acc[type] || 0) + value;
      return acc;
    }, {});

    return Object.entries(totals)
      .map(([type, value]) => ({ type, value }))
      .sort((a, b) => b.value - a.value);
  }, [displayScope]);
  
  // Top country and customer based on active tab
  const { isDarkMode } = useTheme();
  const [splitBy, setSplitBy] = useState('country');
  const [exporterBy, setExporterBy] = useState('fob');

  // The Top Clients tab adds weight and the duty columns, which the shared
  // customer memo does not carry.
  const detailedClientsAll = useMemo(() => {
    const grouped = groupByKey(currentScope, (item) => item.customer || 'Unknown');
    const totalFobAll = currentScope.reduce((sum, i) => sum + i.fobValue, 0);
    return Object.entries(grouped)
      .map(([customer, items]) => ({
        customer,
        shipmentCount: items.length,
        weight: Math.round(items.reduce((a, i) => a + (Number(i.grossWeight) || 0), 0)),
        totalFob: items.reduce((a, i) => a + i.fobValue, 0),
        fcTotalsByType: getFcTotalsByType(items),
        igst: items.reduce((a, i) => a + parseFobValues(i.igst), 0),
        dbk: items.reduce((a, i) => a + (Number(i.dbk) || 0), 0),
        rosctl: items.reduce((a, i) => a + (Number(i.rosctl) || 0), 0),
        percent: totalFobAll ? (items.reduce((a, i) => a + i.fobValue, 0) / totalFobAll) * 100 : 0,
      }))
      .sort((a, b) => b.totalFob - a.totalFob);
  }, [currentScope]);

  const detailedClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    return q ? detailedClientsAll.filter((r) => r.customer.toLowerCase().includes(q)) : detailedClientsAll;
  }, [detailedClientsAll, clientSearch]);

  const clientValueOf = (r) => (clientBy === 'fob' ? r.totalFob : clientBy === 'count' ? r.shipmentCount : r.weight);
  const clientFormat = (v) => (clientBy === 'fob' ? shortINR(v) : clientBy === 'count' ? formatNumber(v) : `${formatNumber(v)} Kg`);
  const rankedClients = useMemo(
    () => [...detailedClientsAll].sort((a, b) => clientValueOf(b) - clientValueOf(a)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detailedClientsAll, clientBy]
  );

  const exportClientsCsv = () => {
    const headers = ['#', 'Client Name', 'Shipments', 'Total Weight (Kg)', 'FOB Value', 'FC Value', 'IGST', 'DBK', 'ROSCTL', '% of Total'];
    const rows = detailedClients.map((r, i) => [
      i + 1, r.customer, r.shipmentCount, r.weight, Math.round(r.totalFob),
      formatFcTotalsInline(r.fcTotalsByType) || '', Math.round(r.igst), Math.round(r.dbk), Math.round(r.rosctl),
      `${shareOf(r).toFixed(1)}%`,
    ]);
    const blob = new Blob([buildCsv(headers, rows)], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `top-clients_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // ── Incentive Insights ────────────────────────────────────────────────
  // "Incentive" here is what the shipment actually claimed back: DBK, RoDTEP
  // and RoSCTL together.
  const incentiveOf = (item) =>
    (Number(item.dbk) || 0) + (Number(item.rodtep) || 0) + (Number(item.rosctl) || 0);

  const incentiveScope = currentScope;

  const incentiveTrendRows = useMemo(() => {
    const buckets = new Map();
    incentiveScope.forEach((item) => {
      const mk = item.monthKey;
      if (!mk || mk === 'Unknown') return;
      const [y, mm] = mk.split('-');
      const key = trendBy === 'year' ? y : trendBy === 'quarter' ? `${y}-Q${Math.floor((Number(mm) - 1) / 3) + 1}` : mk;
      const label = trendBy === 'year' ? y
        : trendBy === 'quarter' ? `Q${Math.floor((Number(mm) - 1) / 3) + 1} ${y}`
        : new Date(Number(y), Number(mm) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const row = buckets.get(key) || { monthKey: key, monthLabel: label, totalFob: 0, shipmentCount: 0 };
      row.totalFob += incentiveOf(item);
      row.shipmentCount += 1;
      buckets.set(key, row);
    });
    return [...buckets.values()].sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  }, [incentiveScope, trendBy]);

  const incentiveByType = useMemo(() => {
    const totals = incentiveScope.reduce((acc, i) => {
      acc.DBK += Number(i.dbk) || 0;
      acc.RoDTEP += Number(i.rodtep) || 0;
      acc.RoSCTL += Number(i.rosctl) || 0;
      return acc;
    }, { DBK: 0, RoDTEP: 0, RoSCTL: 0 });
    const all = totals.DBK + totals.RoDTEP + totals.RoSCTL;
    return Object.entries(totals)
      .filter(([, v]) => v > 0)
      .map(([type, v]) => ({ type, totalFob: v, shipmentCount: 0, percent: all ? (v / all) * 100 : 0 }))
      .sort((a, b) => b.totalFob - a.totalFob);
  }, [incentiveScope]);

  const incentiveRanked = (keyOf) => {
    const grouped = groupByKey(incentiveScope, keyOf);
    const all = incentiveScope.reduce((a, i) => a + incentiveOf(i), 0);
    return Object.entries(grouped)
      .map(([name, items]) => {
        const v = items.reduce((a, i) => a + incentiveOf(i), 0);
        return { name, totalFob: v, shipmentCount: items.length, percent: all ? (v / all) * 100 : 0 };
      })
      .sort((a, b) => b.totalFob - a.totalFob);
  };

  const incentiveByCountry = useMemo(
    () => incentiveRanked((i) => i.country || 'Unknown').map((r) => ({ ...r, country: r.name })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [incentiveScope]
  );
  const incentiveByClient = useMemo(
    () => incentiveRanked((i) => i.customer || 'Unknown').map((r) => ({ ...r, customer: r.name })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [incentiveScope]
  );

  const incentiveDetailsAll = useMemo(() => {
    const grouped = groupByKey(incentiveScope, (i) => i.customer || 'Unknown');
    return Object.entries(grouped)
      .map(([customer, items]) => {
        const fob = items.reduce((a, i) => a + i.fobValue, 0);
        const dbk = items.reduce((a, i) => a + (Number(i.dbk) || 0), 0);
        const rodtep = items.reduce((a, i) => a + (Number(i.rodtep) || 0), 0);
        const rosctl = items.reduce((a, i) => a + (Number(i.rosctl) || 0), 0);
        const total = dbk + rodtep + rosctl;
        return { customer, shipmentCount: items.length, fob, dbk, rodtep, rosctl, total, rate: fob ? (total / fob) * 100 : 0 };
      })
      .filter((r) => r.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [incentiveScope]);

  const incentiveDetails = useMemo(() => {
    const q = incentiveSearch.trim().toLowerCase();
    return q ? incentiveDetailsAll.filter((r) => r.customer.toLowerCase().includes(q)) : incentiveDetailsAll;
  }, [incentiveDetailsAll, incentiveSearch]);

  const exportIncentiveCsv = () => {
    const headers = ['#', 'Client Name', 'Shipments', 'FOB Value', 'DBK', 'RoDTEP', 'RoSCTL', 'Total Incentive', 'Rate %'];
    const rows = incentiveDetails.map((r, i) => [
      i + 1, r.customer, r.shipmentCount, Math.round(r.fob), Math.round(r.dbk),
      Math.round(r.rodtep), Math.round(r.rosctl), Math.round(r.total), `${r.rate.toFixed(2)}%`,
    ]);
    const blob = new Blob([buildCsv(headers, rows)], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `incentives_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const topCountry = countryData[0]?.country || globalCountryData[0]?.country || '—';
  const topCustomer = customerData[0]?.customer || complianceCustomerData[0]?.customer || '—';

  const headerShipments = useMemo(() => {
    return normalizedShipments.map((item) => ({
      ...item,
      type: item.transportType === 'sea' ? 'Sea-Export' : 'Air-Export',
      created_at: item.createdAt
    }));
  }, [normalizedShipments]);

  const resetSelection = () => {
    setSelectedMonthKey(null);
    setSelectedCountry(null);
    gaInsightsResetFilter();
  };

  const renderPieLabel = ({ payload, x, y, cx }) => {
    const pct = payload?.percent ?? 0;
    const fullName = (payload?.country || '').trim();
    const shortName = fullName.length > 14 ? `${fullName.slice(0, 14)}…` : fullName;
    const textAnchor = x > cx ? 'start' : 'end';
    const dx = x > cx ? 8 : -8;

    return (
      <text
        x={x + dx}
        y={y}
        textAnchor={textAnchor}
        dominantBaseline="central"
        fill="#0ea5e9"
        fontSize={12}
        fontWeight={600}
      >
        <title>{`${fullName} ${pct.toFixed(1)}%`}</title>
        <tspan x={x + dx} dy="-0.2em">{shortName}</tspan>
        <tspan x={x + dx} dy="1.2em">{pct.toFixed(1)}%</tspan>
      </text>
    );
  };

  const renderPieTooltip = (valueLabel) => ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 shadow-2xl">
          <p className="text-gray-300 font-semibold mb-3 border-b border-gray-700 pb-2">
            Country: {data.country}
          </p>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <span className="text-cyan-400 text-sm">{valueLabel}:</span>
              <span className="text-white font-bold">{formatCurrency(data.value || 0)}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-amber-400 text-sm">Shipment Count:</span>
              <span className="text-white font-bold">{data.shipmentCount || 0}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-purple-400 text-sm">% of Total:</span>
              <span className="text-white font-bold">{data.percent?.toFixed(1) || 0}%</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <UserShell activeTab="Air-Export">
      <div className="max-w-[1500px] mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-2xl grid place-items-center flex-shrink-0 bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
              <BarChart3 size={21} />
            </span>
            <div>
              <h1 className="text-[22px] font-extrabold text-slate-900 dark:text-white leading-tight">Insights</h1>
              <p className="text-[13px] text-slate-500 dark:text-slate-400">
                Get detailed analytics and business insights for your shipments
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setRangeOpen(true)}
              className={`inline-flex items-center gap-2 h-10 px-4 rounded-xl text-[13px] font-semibold border transition ${
                pageRange
                  ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300'
                  : 'border-slate-200 bg-white text-slate-700 dark:border-slate-700/60 dark:bg-[#111a2e] dark:text-slate-200'
              }`}
            >
              <Calendar size={15} className="text-slate-400" />
              {pageRange
                ? `${fmtRangeDate(pageRange[0].startDate)} – ${fmtRangeDate(pageRange[0].endDate)}`
                : 'All dates'}
            </button>
            {pageRange && (
              <button
                type="button"
                onClick={() => { setPageRange(null); setSelectedMonthKey(null); setSelectedCountry(null); }}
                className="w-9 h-9 grid place-items-center rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:text-white dark:hover:bg-slate-800 transition"
                aria-label="Clear date range"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {rangeOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/55 backdrop-blur-sm p-4"
               onClick={() => setRangeOpen(false)}>
            <div className="w-full max-w-fit rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700"
                 onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between gap-3 px-5 py-3.5 bg-gradient-to-r from-blue-500 to-indigo-600">
                <div className="flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-lg bg-white/20 grid place-items-center"><Calendar size={16} className="text-white" /></span>
                  <div>
                    <p className="text-sm font-bold text-white leading-tight">Insights between two dates</p>
                    <p className="text-[11px] text-blue-50">Filters on the shipment&apos;s S/B date</p>
                  </div>
                </div>
                <button type="button" onClick={() => setRangeOpen(false)}
                        className="text-white/80 hover:text-white hover:bg-white/20 p-1.5 rounded-lg transition">
                  <X size={18} />
                </button>
              </div>

              <div className="ql-daterange overflow-auto">
                <DateRangePicker
                  ranges={pageRange || [{ startDate: new Date(), endDate: new Date(), key: 'selection' }]}
                  onChange={(r) => setPageRange([r.selection])}
                  months={isMobile ? 1 : 2}
                  direction={isMobile ? 'vertical' : 'horizontal'}
                  staticRanges={DATE_PRESETS}
                  inputRanges={[]}
                  rangeColors={['#2563eb']}
                  showDateDisplay
                />
              </div>

              <div className="flex justify-between gap-2.5 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
                <button type="button"
                        onClick={() => { setPageRange(null); setSelectedMonthKey(null); setSelectedCountry(null); setRangeOpen(false); }}
                        className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 dark:text-slate-300 border-2 border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition">
                  All dates
                </button>
                <button type="button"
                        onClick={() => { setSelectedMonthKey(null); setSelectedCountry(null); setRangeOpen(false); }}
                        className="px-5 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-md transition">
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-5 mb-5">

          <div className="w-full">
            <div className="lg:hidden overflow-x-auto scrollbar-hide -mx-1 px-1">
              <div className="flex gap-2 min-w-max pb-2">
                {TRANSPORT_TABS.map((tab) => {
                  const Icon = tab.icon;
                  const on = activeTransportTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => {
                        setActiveTransportTab(tab.id);
                        setSelectedMonthKey(null);
                        setSelectedCountry(null);
                      }}
                      className={`relative flex-shrink-0 flex items-center gap-2.5 min-w-[150px] px-3 py-2.5
                                  rounded-xl border shadow-sm transition
                                  ${on ? `bg-gradient-to-br ${tab.on}` : tab.off}`}
                    >
                      <span className={`grid place-items-center w-9 h-9 rounded-lg flex-shrink-0
                                        ${on ? 'bg-white/20 text-white' : tab.tile}`}>
                        <Icon size={17} />
                      </span>
                      <span className="min-w-0 text-left">
                        <span className={`block text-[13px] font-bold leading-tight truncate
                                          ${on ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                          {tab.label}
                        </span>
                        <span className={`block text-[10.5px] font-medium leading-tight
                                          ${on ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>
                          {tab.subLabel}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="hidden lg:grid grid-cols-3 gap-3">
              {TRANSPORT_TABS.map((tab) => {
                const Icon = tab.icon;
                const on = activeTransportTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      setActiveTransportTab(tab.id);
                      setSelectedMonthKey(null);
                      setSelectedCountry(null);
                    }}
                    className={`group relative overflow-hidden flex items-center gap-3.5 px-4 py-3.5 rounded-2xl
                                border text-left shadow-sm transition duration-150 ease-out
                                hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 active:shadow-sm
                                ${on ? `bg-gradient-to-br ${tab.on}` : tab.off}`}
                  >
                    <span aria-hidden="true"
                      className={`pointer-events-none absolute -right-8 -top-10 w-28 h-28 rounded-full blur-2xl
                                  ${on ? 'bg-white/20' : tab.glow}`} />

                    <span className={`relative grid place-items-center w-11 h-11 rounded-xl flex-shrink-0
                                      ${on ? 'bg-white/20 text-white' : tab.tile}`}>
                      <Icon size={20} />
                    </span>

                    <span className="relative min-w-0 flex-1">
                      <span className={`block text-[15px] font-bold leading-tight
                                        ${on ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                        {tab.label}
                      </span>
                      <span className={`block text-[12px] font-medium leading-tight mt-0.5
                                        ${on ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>
                        {tab.subLabel}
                      </span>
                    </span>

                    <span className={`relative grid place-items-center w-8 h-8 rounded-full flex-shrink-0 transition
                                      group-hover:translate-x-0.5
                                      ${on ? 'bg-white/20 text-white' : tab.arrow}`}>
                      <ArrowUpRight size={16} />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
          <InsightStat
            icon={Package}
            tone="blue"
            value={formatNumber(displayScope.length)}
            label="Total Shipments"
            delta={12}
            seed={displayScope.length + 3}
          />
          <InsightStat
            icon={BarChart3}
            tone="emerald"
            value={shortINR(totalFob)}
            title={formatCurrency(totalFob)}
            label="Total FOB Value"
            delta={18}
            seed={13}
          />
          <InsightStat
            icon={Layers3}
            tone="cyan"
            value={fcTotalsByType.length ? formatFcValue(fcTotalsByType[0].value, fcTotalsByType[0].type) : '—'}
            title="Largest foreign-currency total" 
            label="Total FC Value"
            delta={8}
            seed={7}
          />
          <InsightStat
            icon={Globe}
            tone="violet"
            value={topCountry}
            label="Top Country"
            seed={5}
          />
        </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex flex-wrap gap-2.5">
            {INSIGHT_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSelectedMonthKey(null); setSelectedCountry(null); gaInsightsTabSwitch(tab.id, tab.label); }}
                className={`px-4 h-10 rounded-xl text-[13px] font-bold transition ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white dark:bg-[#111a2e] text-slate-600 dark:text-slate-300 border border-slate-200/80 dark:border-slate-700/50 hover:border-blue-300 dark:hover:border-slate-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {(selectedCountry || selectedMonthKey || pageRange) && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 px-3 h-10 rounded-xl text-[12.5px] font-bold
                               bg-blue-50 text-blue-700 border border-blue-200
                               dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/30">
                Showing: {[
                  pageRange && `${fmtRangeDate(pageRange[0].startDate)} – ${fmtRangeDate(pageRange[0].endDate)}`,
                  selectedCountry,
                  trendRows.find((m) => m.monthKey === selectedMonthKey)?.monthLabel,
                ].filter(Boolean).join(' · ')}
              </span>
              <button
                type="button"
                onClick={() => { resetSelection(); setPageRange(null); }}
                className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl text-[12.5px] font-bold
                           bg-white dark:bg-[#111a2e] text-slate-600 dark:text-slate-300
                           border border-slate-200 dark:border-slate-700/50 hover:border-red-300 hover:text-red-600
                           dark:hover:border-red-500/40 dark:hover:text-red-400 transition"
              >
                <RefreshCw size={15} />
                Clear filters
              </button>
            </div>
          )}
        </div>

        {activeTab === 'performance' && !worldGeoJSON ? (
          <div className="bg-white/90 dark:bg-gray-900/70 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-700">
            <div className="h-[420px] flex items-center justify-center text-gray-500">Loading map data...</div>
          </div>
        ) : activeTab === 'performance' && worldGeoJSON ? (
          <div className="space-y-4 sm:space-y-5">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
              <Panel
                icon={Globe}
                title="Global Presence Map"
                right={<span className="text-[11.5px] text-slate-400">Hover a country for detail</span>}
              >
                <div className="relative w-full h-[360px] sm:h-[420px] rounded-xl overflow-hidden
                                bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700/50">
                  <BestMap
                    worldGeoJSON={worldGeoJSON}
                    countryDataLookup={countryDataLookup}
                    colorScale={colorScale}
                    formatCurrency={formatCurrency}
                    formatFcTotalsInline={formatFcTotalsInline}
                    normalizeCountry={normalizeCountry}
                    setHoveredCountry={setHoveredCountry}
                    hoveredCountry={hoveredCountry}
                    isDark={isDarkMode}
                  />
                </div>
                <div className="flex items-center gap-2.5 mt-3 text-[11.5px] font-semibold text-slate-500 dark:text-slate-400">
                  Lower
                  <span className="h-2 w-40 rounded-full bg-gradient-to-r from-blue-100 to-blue-700" />
                  Higher
                  <span className="text-slate-400">(FOB Value)</span>
                </div>
              </Panel>

              <Panel
                icon={BarChart3}
                title="Country Trend"
                right={
                  <Segmented
                    options={[['month', 'Month'], ['quarter', 'Quarter'], ['year', 'Year']]}
                    value={trendBy}
                    onChange={(v) => { setTrendBy(v); setSelectedMonthKey(null); }}
                  />
                }
              >
                <p className="-mt-2 mb-2 text-[12px] text-slate-500 dark:text-slate-400">
                  {selectedCountry ? `Monthly trend for ${selectedCountry}` : 'All countries — pick one below to narrow it'}
                </p>
                <TrendChart
                  rows={trendRows}
                  selectedKey={selectedMonthKey}
                  onPick={(k) => setSelectedMonthKey((prev) => (prev === k ? null : k))}
                  isDark={isDarkMode}
                  height="h-[330px] sm:h-[400px]"
                />
              </Panel>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
              <Panel
                icon={BarChart3}
                title="Top Countries by FOB Value"
                right={<span className="text-[11.5px] text-slate-400">Click a row to filter</span>}
              >
                <RankTable
                  onPick={(c) => setSelectedCountry((prev) => (prev === c ? null : c))}
                  activeKey={selectedCountry}
                  rows={globalCountryData.slice(0, 8)}
                  nameKey="country"
                  columns={[
                    { head: 'FOB Value', cell: (r) => shortINR(r.fobValue ?? r.totalFob), align: 'right' },
                    { head: 'Shipments', cell: (r) => formatNumber(r.shipmentCount), align: 'right' },
                  ]}
                />
              </Panel>

              <Panel
                icon={Users}
                title={selectedCountry ? `Top Clients in ${selectedCountry}` : 'Top Clients'}
              >
                <RankTable
                  rows={customerData.slice(0, 8)}
                  nameKey="customer"
                  columns={[
                    { head: 'Shipments', cell: (r) => formatNumber(r.shipmentCount), align: 'right' },
                    { head: 'FOB Value', cell: (r) => shortINR(r.totalFob), align: 'right' },
                  ]}
                />
              </Panel>
            </div>
          </div>
        ) : activeTab === 'compliance' ? (
          <div className="space-y-4 sm:space-y-5">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
              <Panel
                icon={BarChart3}
                title="Client Wise Trend"
                right={
                  <Segmented
                    options={[['month', 'Month'], ['quarter', 'Quarter'], ['year', 'Year']]}
                    value={trendBy}
                    onChange={(v) => { setTrendBy(v); setSelectedMonthKey(null); }}
                  />
                }
              >
                <p className="-mt-2 mb-2 text-[12px] text-slate-500 dark:text-slate-400">
                  {selectedCountry ? `Clients in ${selectedCountry}` : 'All clients in the current scope'}
                </p>
                <TrendChart
                  rows={trendRows}
                  selectedKey={selectedMonthKey}
                  onPick={(k) => setSelectedMonthKey((prev) => (prev === k ? null : k))}
                  isDark={isDarkMode}
                />
              </Panel>

              <Panel icon={PieIcon} title="Client Distribution by FOB Value">
                <DonutSplit rows={complianceCustomerData} nameKey="customer" />
              </Panel>

              <Panel
                icon={Globe}
                title="Top Clients by Country"
                right={<span className="text-[11.5px] text-slate-400">Click a row to filter</span>}
              >
                <RankTable
                  onPick={(c) => setSelectedCountry((prev) => (prev === c ? null : c))}
                  activeKey={selectedCountry}
                  rows={countryData.slice(0, 7)}
                  nameKey="country"
                  columns={[
                    { head: 'FOB Value', cell: (r) => shortINR(r.totalFob), align: 'right' },
                    { head: 'Shipments', cell: (r) => formatNumber(r.shipmentCount), align: 'right' },
                  ]}
                />
              </Panel>

              <Panel
                icon={BarChart3}
                title={`Top Clients by ${clientBy === 'fob' ? 'FOB Value' : clientBy === 'count' ? 'Shipments' : 'Weight'}`}
                right={
                  <Segmented
                    options={[['fob', 'By FOB Value'], ['count', 'By Shipments'], ['weight', 'By Weight']]}
                    value={clientBy}
                    onChange={setClientBy}
                  />
                }
              >
                <BarList
                  rows={rankedClients.slice(0, 7)}
                  nameKey="customer"
                  valueOf={clientValueOf}
                  format={clientFormat}
                />
              </Panel>
            </div>

            <Panel
              icon={Users}
              title="Top Clients — Detailed View"
              right={
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      placeholder="Search client..."
                      className="h-9 w-48 pl-8 pr-3 rounded-xl text-[12.5px] bg-white dark:bg-slate-800/60
                                 border border-slate-200 dark:border-slate-700/70 text-slate-700 dark:text-slate-200
                                 placeholder-slate-400 outline-none focus:border-blue-400 transition"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={exportClientsCsv}
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-xl text-[12.5px] font-bold
                               border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100
                               dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-300"
                  >
                    <Download size={14} /> Export CSV
                  </button>
                </div>
              }
            >
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px] whitespace-nowrap">
                  <thead>
                    <tr className="text-slate-400 dark:text-slate-500">
                      {['#', 'Client Name', 'Shipments', 'Total Weight (Kg)', 'FOB Value', 'FC Value', 'IGST', 'DBK', 'ROSCTL', '% of Total'].map((h, i) => (
                        <th key={h} className={`font-semibold pb-2 ${i > 1 ? 'text-right px-3' : 'text-left pr-3'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detailedClients.length === 0 ? (
                      <tr><td colSpan={10} className="py-10 text-center text-slate-400">No client matches this search.</td></tr>
                    ) : detailedClients.map((r, i) => (
                      <tr key={r.customer} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="py-2.5 text-slate-400 font-semibold">{i + 1}</td>
                        <td className="py-2.5 pr-3 font-bold text-slate-800 dark:text-slate-100 max-w-[220px] truncate" title={r.customer}>{r.customer}</td>
                        <td className="py-2.5 px-3 text-right font-semibold text-slate-600 dark:text-slate-300">{formatNumber(r.shipmentCount)}</td>
                        <td className="py-2.5 px-3 text-right font-semibold text-slate-600 dark:text-slate-300">{formatNumber(r.weight)}</td>
                        <td className="py-2.5 px-3 text-right font-bold text-slate-800 dark:text-slate-100">{shortINR(r.totalFob)}</td>
                        <td className="py-2.5 px-3 text-right font-semibold text-slate-600 dark:text-slate-300">{formatFcTotalsInline(r.fcTotalsByType) || '—'}</td>
                        <td className="py-2.5 px-3 text-right font-semibold text-slate-600 dark:text-slate-300">{shortINR(r.igst)}</td>
                        <td className="py-2.5 px-3 text-right font-semibold text-slate-600 dark:text-slate-300">{shortINR(r.dbk)}</td>
                        <td className="py-2.5 px-3 text-right font-semibold text-slate-600 dark:text-slate-300">{shortINR(r.rosctl)}</td>
                        <td className="py-2.5 px-3 text-right">
                          <span className="inline-block px-2 py-0.5 rounded-lg text-[11.5px] font-bold bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
                            {shareOf(r).toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        ) : activeTab === 'forecasts' ? (
          <div className="space-y-4 sm:space-y-5">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
              <Panel
                icon={BarChart3}
                title="Incentive Trend"
                right={
                  <Segmented
                    options={[['month', 'Month'], ['quarter', 'Quarter'], ['year', 'Year']]}
                    value={trendBy}
                    onChange={(v) => { setTrendBy(v); setSelectedMonthKey(null); }}
                  />
                }
              >
                <p className="-mt-2 mb-2 text-[12px] text-slate-500 dark:text-slate-400">
                  DBK + RoDTEP + RoSCTL claimed, against shipment count
                </p>
                <TrendChart
                  rows={incentiveTrendRows}
                  selectedKey={selectedMonthKey}
                  onPick={(k) => setSelectedMonthKey((prev) => (prev === k ? null : k))}
                  isDark={isDarkMode}
                  valueLabel="Incentive (₹)"
                />
              </Panel>

              <Panel icon={PieIcon} title="Incentive by Type">
                <DonutSplit rows={incentiveByType} nameKey="type" />
              </Panel>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
              <Panel
                icon={Globe}
                title="Top Countries by Incentive"
                right={<span className="text-[11.5px] text-slate-400">Click a row to filter</span>}
              >
                <RankTable
                  onPick={(c) => setSelectedCountry((prev) => (prev === c ? null : c))}
                  activeKey={selectedCountry}
                  rows={incentiveByCountry.slice(0, 7)}
                  nameKey="country"
                  columns={[
                    { head: 'Incentive', cell: (r) => shortINR(r.totalFob), align: 'right' },
                    { head: 'Shipments', cell: (r) => formatNumber(r.shipmentCount), align: 'right' },
                  ]}
                />
              </Panel>

              <Panel icon={Users} title={selectedCountry ? `Top Clients by Incentive — ${selectedCountry}` : 'Top Clients by Incentive'}>
                <RankTable
                  rows={incentiveByClient.slice(0, 7)}
                  nameKey="customer"
                  columns={[
                    { head: 'Incentive', cell: (r) => shortINR(r.totalFob), align: 'right' },
                    { head: 'Shipments', cell: (r) => formatNumber(r.shipmentCount), align: 'right' },
                  ]}
                />
              </Panel>
            </div>

            <Panel
              icon={Layers3}
              title="Incentive Details"
              right={
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={incentiveSearch}
                      onChange={(e) => setIncentiveSearch(e.target.value)}
                      placeholder="Search client..."
                      className="h-9 w-48 pl-8 pr-3 rounded-xl text-[12.5px] bg-white dark:bg-slate-800/60
                                 border border-slate-200 dark:border-slate-700/70 text-slate-700 dark:text-slate-200
                                 placeholder-slate-400 outline-none focus:border-blue-400 transition"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={exportIncentiveCsv}
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-xl text-[12.5px] font-bold
                               border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100
                               dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-300"
                  >
                    <Download size={14} /> Export CSV
                  </button>
                </div>
              }
            >
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px] whitespace-nowrap">
                  <thead>
                    <tr className="text-slate-400 dark:text-slate-500">
                      {['#', 'Client Name', 'Shipments', 'FOB Value', 'DBK', 'RoDTEP', 'RoSCTL', 'Total Incentive', 'Rate %'].map((h, i) => (
                        <th key={h} className={`font-semibold pb-2 ${i > 1 ? 'text-right px-3' : 'text-left pr-3'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {incentiveDetails.length === 0 ? (
                      <tr><td colSpan={9} className="py-10 text-center text-slate-400">No incentive recorded in this scope.</td></tr>
                    ) : incentiveDetails.map((r, i) => (
                      <tr key={r.customer} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="py-2.5 text-slate-400 font-semibold">{i + 1}</td>
                        <td className="py-2.5 pr-3 font-bold text-slate-800 dark:text-slate-100 max-w-[220px] truncate" title={r.customer}>{r.customer}</td>
                        <td className="py-2.5 px-3 text-right font-semibold text-slate-600 dark:text-slate-300">{formatNumber(r.shipmentCount)}</td>
                        <td className="py-2.5 px-3 text-right font-semibold text-slate-600 dark:text-slate-300">{shortINR(r.fob)}</td>
                        <td className="py-2.5 px-3 text-right font-semibold text-slate-600 dark:text-slate-300">{shortINR(r.dbk)}</td>
                        <td className="py-2.5 px-3 text-right font-semibold text-slate-600 dark:text-slate-300">{shortINR(r.rodtep)}</td>
                        <td className="py-2.5 px-3 text-right font-semibold text-slate-600 dark:text-slate-300">{shortINR(r.rosctl)}</td>
                        <td className="py-2.5 px-3 text-right font-bold text-slate-800 dark:text-slate-100">{shortINR(r.total)}</td>
                        <td className="py-2.5 px-3 text-right">
                          <span className="inline-block px-2 py-0.5 rounded-lg text-[11.5px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                            {r.rate.toFixed(2)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        ) : activeTab === 'igst' ? (
          <IGSTTab invoices={invoices} shipments={normalizedShipments} />
        ) : activeTab !== 'overview' ? (
          <div className="bg-white/90 dark:bg-gray-900/70 rounded-2xl shadow-lg p-10 text-center border border-gray-100 dark:border-gray-700">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Coming Soon</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-2">This insight tab is under development.</p>
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-5">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
              <Panel
                icon={BarChart3}
                title="Export Trend"
                right={
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setRangeOpen(true)}
                      className={`inline-flex items-center gap-2 h-9 px-3 rounded-xl text-[12px] font-bold border transition ${
                        pageRange
                          ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300'
                          : 'border-slate-200 bg-white text-slate-600 dark:border-slate-700/60 dark:bg-slate-800/60 dark:text-slate-300'
                      }`}
                    >
                      <Calendar size={14} className="text-slate-400" />
                      {pageRange
                        ? `${fmtRangeDate(pageRange[0].startDate)} – ${fmtRangeDate(pageRange[0].endDate)}`
                        : 'All dates'}
                    </button>
                    {pageRange && (
                      <button
                        type="button"
                        onClick={() => { setPageRange(null); setSelectedMonthKey(null); setSelectedCountry(null); }}
                        className="w-8 h-8 grid place-items-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:text-white dark:hover:bg-slate-800 transition"
                        aria-label="Clear date range"
                      >
                        <X size={14} />
                      </button>
                    )}
                    <Segmented
                      options={[['month', 'Month'], ['quarter', 'Quarter'], ['year', 'Year']]}
                      value={trendBy}
                      onChange={(v) => { setTrendBy(v); setSelectedMonthKey(null); setSelectedCountry(null); }}
                    />
                  </div>
                }
              >
                <TrendChart
                  rows={trendRows}
                  selectedKey={selectedMonthKey}
                  onPick={(k) => { setSelectedMonthKey((prev) => (prev === k ? null : k)); setSelectedCountry(null); }}
                  isDark={isDarkMode}
                />
              </Panel>

              <Panel
                icon={PieIcon}
                title="Shipment Distribution"
                right={<Segmented options={[['country', 'By Country'], ['client', 'By Client']]} value={splitBy} onChange={setSplitBy} />}
              >
                <DonutSplit rows={splitBy === 'country' ? countryData : customerData} nameKey={splitBy === 'country' ? 'country' : 'customer'} />
              </Panel>

              <Panel
                icon={Globe}
                title="Top Countries"
                right={<span className="text-[11.5px] text-slate-400">Click a row to filter</span>}
              >
                <RankTable
                  onPick={(c) => setSelectedCountry((prev) => (prev === c ? null : c))}
                  activeKey={selectedCountry}
                  rows={countryData.slice(0, 6)}
                  nameKey="country"
                  columns={[
                    { head: 'FOB Value', cell: (r) => shortINR(r.totalFob), align: 'right' },
                    { head: 'Shipments', cell: (r) => formatNumber(r.shipmentCount), align: 'right' },
                  ]}
                />
              </Panel>

              <Panel
                icon={Users}
                title={selectedCountry ? `Top Clients in ${selectedCountry}` : 'Top Clients'}
              >
                <RankTable
                  rows={customerData.slice(0, 6)}
                  nameKey="customer"
                  columns={[
                    { head: 'Shipments', cell: (r) => formatNumber(r.shipmentCount), align: 'right' },
                    { head: 'FOB Value', cell: (r) => shortINR(r.totalFob), align: 'right' },
                  ]}
                />
              </Panel>
            </div>
          </div>
        )}
      </div>
    </UserShell>
  );
};

// ─── IGST Tab Component ───────────────────────────────────────────────────────
const IGST_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899'];

const sumArray = (val) => {
  if (!val) return 0;
  if (Array.isArray(val)) return val.reduce((s, v) => s + (parseFloat(v) || 0), 0);
  return parseFloat(val) || 0;
};

const IGSTTab = ({ invoices, shipments }) => {
  const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const formatDateRange = () => {
    if (!dateRange || !dateRange[0]) return 'Filter by date range';
    const start = dateRange[0].startDate;
    const end = dateRange[0].endDate;
    if (!start || !end) return 'Filter by date range';
    const fmt2 = (d) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${fmt2(start)} — ${fmt2(end)}`;
  };

  const isInDateRange = (dateStr) => {
    if (!dateRange || !dateRange[0]?.startDate || !dateRange[0]?.endDate) return true;
    const d = new Date(dateStr || '');
    if (isNaN(d)) return true;
    const start = new Date(dateRange[0].startDate); start.setHours(0, 0, 0, 0);
    const end = new Date(dateRange[0].endDate); end.setHours(23, 59, 59, 999);
    return d >= start && d <= end;
  };

  // Shipment-level IGST data
  const shipmentData = useMemo(() => {
    return (shipments || [])
      .map(s => ({
        awb: s.awbNo || s.awb_no || '—',
        customer: s.customer || 'Unknown',
        country: s.country || 'Unknown',
        igst: sumArray(s.igst),
        fob: Number(s.fobValue) || 0,
        rawDate: s.createdAt || '',
        month: s.monthLabel || 'Unknown',
        monthKey: s.monthKey || 'Unknown',
      }))
      .filter(d => d.igst > 0);
  }, [shipments]);

  // Invoice-level data
  const invoiceData = useMemo(() => {
    return (invoices || [])
      .map(inv => ({
        customer: inv.customer_name || inv.customerName || inv.username || 'Unknown',
        igst: Number(inv.summary?.igst || 0),
        fob: Number(inv.summary?.taxable_amount ?? inv.summary?.taxableAmount ?? inv.summary?.grand_total ?? 0),
        cgst: Number(inv.summary?.cgst || 0),
        sgst: Number(inv.summary?.sgst || 0),
        grandTotal: Number(inv.summary?.grand_total || 0),
        awb: inv.awb_number || inv.awbNumber || inv.bl_number || '—',
        rawDate: inv.created_at || inv.createdAt || '',
        month: (() => {
          const d = new Date(inv.created_at || inv.createdAt || '');
          return isNaN(d) ? 'Unknown' : d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
        })(),
      }))
      .filter(d => d.igst > 0 || d.cgst > 0 || d.sgst > 0);
  }, [invoices]);

  const allData = shipmentData.length > 0 ? shipmentData : invoiceData;

  // Apply date range filter first, then month filter
  const dateFilteredData = useMemo(() =>
    allData.filter(d => isInDateRange(d.rawDate)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [allData, dateRange]);

  const monthScoped = useMemo(() =>
    selectedMonth ? dateFilteredData.filter(d => d.month === selectedMonth) : dateFilteredData,
  [dateFilteredData, selectedMonth]);

  const byClient = (rows) => (selectedClient ? rows.filter(d => d.customer === selectedClient) : rows);
  const byCountry = (rows) => (selectedCountry ? rows.filter(d => (d.country || 'Other') === selectedCountry) : rows);

  // Everything but a table reads the fully narrowed set; each table leaves its
  // own filter out, so picking a row never empties the list you picked from.
  const data = useMemo(() => byCountry(byClient(monthScoped)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [monthScoped, selectedClient, selectedCountry]);

  const clientScope = useMemo(() => byCountry(monthScoped),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [monthScoped, selectedCountry]);

  const countryScope = useMemo(() => byClient(monthScoped),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [monthScoped, selectedClient]);

  const totalIGST = useMemo(() => data.reduce((s, d) => s + d.igst, 0), [data]);
  const totalCGST = useMemo(() => data.reduce((s, d) => s + (d.cgst||0), 0), [data]);
  const totalSGST = useMemo(() => data.reduce((s, d) => s + (d.sgst||0), 0), [data]);

  // Monthly chart — only valid past/current months shown as bars
  const monthlyData = useMemo(() => {
    const now = new Date();
    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const map = {};
    dateFilteredData
      .filter(d => d.monthKey && d.monthKey !== 'Unknown' && d.monthKey <= todayKey)
      .forEach(d => {
        if (!map[d.monthKey]) map[d.monthKey] = { month: d.month, monthKey: d.monthKey, igst: 0, fob: 0, count: 0 };
        map[d.monthKey].igst += d.igst;
        map[d.monthKey].fob += d.fob || 0;
        map[d.monthKey].count += 1;
      });
    return Object.values(map).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  }, [dateFilteredData]);

  const customerData = useMemo(() => {
    const map = {};
    clientScope.forEach(d => {
      if (!map[d.customer]) map[d.customer] = { customer: d.customer, igst: 0, count: 0 };
      map[d.customer].igst += d.igst;
      map[d.customer].count += 1;
    });
    return Object.values(map).sort((a, b) => b.igst - a.igst).slice(0, 8);
  }, [clientScope]);

  const countryData = useMemo(() => {
    const map = {};
    countryScope.forEach(d => {
      const c = d.country && d.country !== 'Unknown' ? d.country : 'Other';
      if (!map[c]) map[c] = { country: c, igst: 0, count: 0 };
      map[c].igst += d.igst;
      map[c].count += 1;
    });
    const arr = Object.values(map).sort((a, b) => b.igst - a.igst);
    const grandTotal = arr.reduce((s, d) => s + d.igst, 0);
    return arr.map(d => ({ ...d, percent: grandTotal > 0 ? (d.igst / grandTotal) * 100 : 0 }));
  }, [countryScope]);

  const splitPie = [
    { name: 'IGST', value: totalIGST },
    { name: 'CGST', value: totalCGST },
    { name: 'SGST', value: totalSGST },
  ].filter(d => d.value > 0);

  const hasFilters = selectedMonth || dateRange || selectedClient || selectedCountry;

  const { isDarkMode: isDark } = useTheme();
  const [igstMode, setIgstMode] = useState('amount');
  const [igstSearch, setIgstSearch] = useState('');

  // The shared panels want { name, totalFob, shipmentCount, percent }, so the
  // IGST aggregates are reshaped into that once rather than at each use.
  const asPanelRows = (arr, nameKey, valueKey) => {
    const all = arr.reduce((a, r) => a + (r[valueKey] || 0), 0);
    return arr.map((r) => ({
      ...r,
      [nameKey]: r[nameKey],
      totalFob: r[valueKey] || 0,
      shipmentCount: r.count || 0,
      percent: all ? ((r[valueKey] || 0) / all) * 100 : 0,
    }));
  };

  const igstClientRows = useMemo(() => asPanelRows(customerData, 'customer', 'igst'), [customerData]);
  const igstCountryRows = useMemo(() => asPanelRows(countryData, 'country', 'igst'), [countryData]);

  const igstTrendRows = useMemo(() => monthlyData.map((m) => ({
    monthKey: m.monthKey,
    monthLabel: m.month,
    totalFob: igstMode === 'amount' ? m.igst : (m.fob ? (m.igst / m.fob) * 100 : 0),
    shipmentCount: m.count || 0,
  })), [monthlyData, igstMode]);

  const igstRows = useMemo(() => {
    const q = igstSearch.trim().toLowerCase();
    if (!q) return data;
    return data.filter((d) =>
      String(d.awb).toLowerCase().includes(q) || String(d.customer).toLowerCase().includes(q));
  }, [data, igstSearch]);

  const exportIgstCsv = () => {
    const headers = ['#', 'AWB / B/L No.', 'Client Name', 'Country', 'Invoice Total', 'IGST', 'CGST', 'SGST', 'Date'];
    const rows = igstRows.map((r, i) => [
      i + 1, r.awb, r.customer, r.country || '', Math.round(r.grandTotal),
      Math.round(r.igst), Math.round(r.cgst), Math.round(r.sgst), r.month,
    ]);
    const blob = new Blob([buildCsv(headers, rows)], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `igst_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (allData.length === 0) return (
    <div className="bg-white/90 dark:bg-gray-900/70 rounded-2xl shadow-lg p-10 text-center border border-gray-100 dark:border-gray-700">
      <p className="text-xl font-semibold text-gray-700 dark:text-gray-300">No IGST data found</p>
      <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">IGST data will appear once shipments with IGST are loaded.</p>
    </div>
  );

  const facets = [
    ...(selectedMonth ? [{ key: 'm', label: `Month: ${selectedMonth}`, clear: () => setSelectedMonth(null) }] : []),
    ...(selectedClient ? [{ key: 'c', label: `Client: ${selectedClient}`, clear: () => setSelectedClient(null) }] : []),
    ...(selectedCountry ? [{ key: 'n', label: `Country: ${selectedCountry}`, clear: () => setSelectedCountry(null) }] : []),
    ...(dateRange ? [{ key: 'd', label: formatDateRange(), clear: () => setDateRange(null) }] : []),
  ];

  return (
    <div className="space-y-4 sm:space-y-5">
      {hasFilters && (
        <div className="flex flex-wrap items-center gap-2 px-4 sm:px-5 py-3 rounded-2xl
                        border border-slate-200 bg-white shadow-sm
                        dark:border-slate-800 dark:bg-slate-900/60">
          <span className="text-[10.5px] font-black uppercase tracking-[0.12em] text-slate-400">Filters</span>
          {facets.map((f) => (
            <button key={f.key} type="button" onClick={f.clear}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-bold
                         bg-blue-50 text-blue-700 hover:bg-blue-100 transition
                         dark:bg-blue-500/15 dark:text-blue-300 dark:hover:bg-blue-500/25">
              {f.label}
              <X size={12} />
            </button>
          ))}
          <button type="button"
            onClick={() => { setSelectedMonth(null); setSelectedClient(null); setSelectedCountry(null); setDateRange(null); }}
            className="text-[11.5px] font-bold text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 underline">
            Clear all
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
        <Panel
          icon={BarChart3}
          title="IGST Trend"
          right={
            <Segmented
              options={[['amount', 'IGST Amount'], ['pct', 'IGST % of FOB']]}
              value={igstMode}
              onChange={setIgstMode}
            />
          }
        >
          <p className="-mt-2 mb-2 text-[12px] text-slate-500 dark:text-slate-400">
            {igstMode === 'amount' ? 'IGST claimed each month' : 'IGST as a share of the month\'s FOB value'}
          </p>
          <TrendChart
            rows={igstTrendRows}
            selectedKey={null}
            onPick={() => {}}
            isDark={isDark}
            valueLabel={igstMode === 'amount' ? 'IGST (₹)' : 'IGST % of FOB'}
            valueFormat={igstMode === 'amount' ? shortINR : (v) => `${Number(v).toFixed(1)}%`}
          />
        </Panel>

        <Panel icon={PieIcon} title="IGST by Country">
          <DonutSplit rows={igstCountryRows} nameKey="country" />
        </Panel>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
        <Panel
          icon={Users}
          title="Top Clients by IGST"
          right={<span className="text-[11.5px] text-slate-400">Click a row to filter</span>}
        >
          <RankTable
            onPick={(c) => setSelectedClient((prev) => (prev === c ? null : c))}
            activeKey={selectedClient}
            rows={igstClientRows.slice(0, 7)}
            nameKey="customer"
            columns={[
              { head: 'IGST', cell: (r) => shortINR(r.totalFob), align: 'right' },
              { head: 'Invoices', cell: (r) => formatNumber(r.shipmentCount), align: 'right' },
            ]}
          />
        </Panel>

        <Panel
          icon={Globe}
          title="IGST Share by Country"
          right={<span className="text-[11.5px] text-slate-400">Click a row to filter</span>}
        >
          <RankTable
            onPick={(c) => setSelectedCountry((prev) => (prev === c ? null : c))}
            activeKey={selectedCountry}
            rows={igstCountryRows.slice(0, 7)}
            nameKey="country"
            columns={[
              { head: 'IGST', cell: (r) => shortINR(r.totalFob), align: 'right' },
              { head: 'Invoices', cell: (r) => formatNumber(r.shipmentCount), align: 'right' },
            ]}
          />
        </Panel>
      </div>

      <Panel
        icon={Layers3}
        title="IGST Detailed Report"
        right={
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={igstSearch}
                onChange={(e) => setIgstSearch(e.target.value)}
                placeholder="Search AWB or client..."
                className="h-9 w-56 pl-8 pr-3 rounded-xl text-[12.5px] bg-white dark:bg-slate-800/60
                           border border-slate-200 dark:border-slate-700/70 text-slate-700 dark:text-slate-200
                           placeholder-slate-400 outline-none focus:border-blue-400 transition"
              />
            </div>
            <button
              type="button"
              onClick={exportIgstCsv}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-xl text-[12.5px] font-bold
                         border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100
                         dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-300"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px] whitespace-nowrap">
            <thead>
              <tr className="text-slate-400 dark:text-slate-500">
                {['#', 'AWB / B/L No.', 'Client Name', 'Country', 'Invoice Total', 'IGST', 'CGST', 'SGST', 'Date'].map((h, i) => (
                  <th key={h} className={`font-semibold pb-2 ${i > 3 ? 'text-right px-3' : 'text-left pr-3'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {igstRows.length === 0 ? (
                <tr><td colSpan={9} className="py-10 text-center text-slate-400">No invoice matches this search.</td></tr>
              ) : igstRows.map((r, i) => (
                <tr key={`${r.awb}-${i}`} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="py-2.5 text-slate-400 font-semibold">{i + 1}</td>
                  <td className="py-2.5 pr-3 font-mono font-bold text-slate-800 dark:text-slate-100">{r.awb}</td>
                  <td className="py-2.5 pr-3 font-semibold text-slate-700 dark:text-slate-200 max-w-[200px] truncate" title={r.customer}>{r.customer}</td>
                  <td className="py-2.5 pr-3 text-slate-600 dark:text-slate-300">{r.country || '—'}</td>
                  <td className="py-2.5 px-3 text-right font-semibold text-slate-600 dark:text-slate-300">{shortINR(r.grandTotal)}</td>
                  <td className="py-2.5 px-3 text-right font-bold text-slate-800 dark:text-slate-100">{shortINR(r.igst)}</td>
                  <td className="py-2.5 px-3 text-right font-semibold text-slate-600 dark:text-slate-300">{shortINR(r.cgst)}</td>
                  <td className="py-2.5 px-3 text-right font-semibold text-slate-600 dark:text-slate-300">{shortINR(r.sgst)}</td>
                  <td className="py-2.5 px-3 text-right text-slate-500 dark:text-slate-400">{r.month}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
};

export default AirExportInsights;
